"use client";

/**
 * @file Features Grid Component
 * @description Feature showcase area in the homepage, displaying all main platform features in a grid format
 * Includes scroll animation effects, where cards fade in when the user scrolls to this area
 * Using "use client" directive to mark as a client component, as it requires interaction and animation effects
 */

import { useEffect, useRef } from 'react';
import Image from 'next/image';

/**
 * Features Grid Component
 * @description Grid layout displaying all platform feature modules
 * @returns {JSX.Element} Features grid component
 */
export default function FeaturesGrid() {
  // Create a reference to the features area for implementing intersection observer functionality
  const featuresRef = useRef<HTMLDivElement>(null);

  // Feature data removed as it's not currently used in the component

  /**
   * Scroll display animation effect
   * @description When the user scrolls to the feature card area, add fade-in animation effect
   */
  useEffect(() => {
    // Create an intersection observer instance to detect if elements are visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When the element enters the viewport, add the fade-in animation class
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 } // Trigger callback when at least 10% of the element is visible
    );

    // Save the current ref value for use in the cleanup function
    const currentFeaturesRef = featuresRef.current;

    // Start observing all feature card elements
    if (currentFeaturesRef) {
      const featureElements = currentFeaturesRef.querySelectorAll('.feature-card');
      featureElements.forEach((el) => observer.observe(el));
    }

    // Clear the observer when the component unmounts to avoid memory leaks
    return () => {
      if (currentFeaturesRef) {
        const featureElements = currentFeaturesRef.querySelectorAll('.feature-card');
        featureElements.forEach((el) => observer.unobserve(el));
      }
    };
  }, []);

  return (
    // Feature area container, set background color and ID for easy navigation
    <section className="py-20 bg-gray-50 dark:bg-gray-900" id="features">
      {/* Content container */}
      <div className="container mx-auto px-4">
        {/* Title area */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Transform Your Pets into Humans
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Discover the amazing features of Pet2Human AI and see your furry friends in a whole new light
          </p>
        </div>

        {/* Large showcase image at the bottom - direct image without animation */}
        <div className="mt-16 w-full relative max-w-5xl mx-auto" style={{ height: '400px' }}>
          <Image
            src="/images/screenshot_2025-05-06_11-11-40.png"
            alt="Pet2Human AI Examples"
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
            style={{ objectFit: 'contain' }}
            priority
          />
        </div>
      </div>
    </section>
  );
}